import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { isAssignableUser, type UserLookup } from "@/hooks/use-users";
import { useBulkAssignPhase, type BulkAssignLine } from "@/hooks/use-bulk-assign-phase";

/**
 * VIB-1207: «Tildel alle til …»-handling i fase-headeren på Timetildeling.
 * Én person velges, og alle interne oppgavelinjer i fasen som ikke allerede
 * er tildelt vedkommende fylles opp (rest-timer). Eksisterende tildelinger
 * med status-historikk røres aldri (ren INSERT, se `useBulkAssignPhase`).
 *
 * Kun synlig når `canAssignHours && !readOnly` (samme gate som enkelt-
 * tildeling). Rendres i header-båndet i `BudgetPhaseGroup`.
 */
export function BulkAssignPhaseButton({
  projectId,
  users,
  lines,
}: {
  projectId: string;
  users: UserLookup[];
  /** Interne oppgavelinjer i fasen (id + budsjetterte timer). */
  lines: BulkAssignLine[];
}) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const bulkAssign = useBulkAssignPhase();
  const assignableUsers = users.filter(isAssignableUser);

  const totalHours = lines.reduce((s, l) => s + (l.quantityHours ?? 0), 0);
  const selectedName = assignableUsers.find((u) => u.id === userId)?.full_name;

  const handleAssign = () => {
    if (!userId) return;
    bulkAssign.mutate(
      { projectId, userId, lines },
      {
        onSuccess: () => {
          setOpen(false);
          setUserId("");
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors px-1.5 py-0.5 rounded border border-border/60 hover:border-primary/40"
          title="Tildel alle oppgaver i fasen til én person"
        >
          <SrIcon name="users" className="w-3 h-3" />
          Tildel alle
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">
          Tildel alle {lines.length} oppgaver ({totalHours}t) i fasen til:
        </p>
        <select
          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        >
          <option value="">Velg ansatt…</option>
          {assignableUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name}
            </option>
          ))}
        </select>
        <p className="text-[10.5px] text-muted-foreground leading-snug">
          Fyller kun tomme linjer — oppgaver {selectedName ?? "personen"} allerede
          er tildelt, eller som er fullt fordelt til andre, hoppes over.
        </p>
        <div className="flex justify-end gap-2 pt-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setOpen(false)}
            disabled={bulkAssign.isPending}
          >
            Avbryt
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={handleAssign}
            disabled={!userId || bulkAssign.isPending}
          >
            {bulkAssign.isPending ? (
              <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <SrIcon name="check" className="w-3 h-3 mr-1" />
            )}
            Tildel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
